import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import UserDashboardLayout from '../../components/dashboard/UserDashboardLayout';
import { API_BASE } from '../../utils/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';

interface Lead {
    _id: string;
    name: string;
    mobile: string;
    email: string;
    status: string;
    source: string;
    city: string;
    budget: string;
    propertyType: string;
    suggestedProjects?: { _id: string; name: string; slug?: string; developer?: { name: string; email: string; phone: string } }[];
    timeline: { status: string; notes: string; date: string }[];
    siteVisit?: {
        date: string;
        time: string;
        agentName: string;
        project?: { _id: string; name: string; slug?: string; developer?: { name: string; email: string; phone: string } };
        notes: string;
    };
    unlockedBy?: string[];
    createdAt: string;
}

export default function BrokerLeads() {
    const { token, user } = useAuth();
    const [leads, setLeads] = useState<Lead[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [viewLead, setViewLead] = useState<Lead | null>(null);

    useEffect(() => {
        if (token) fetchLeads();
    }, [token]);

    const fetchLeads = async () => {
        setLoading(true);
        try {
            const [leadsRes, subsRes] = await Promise.all([
                fetch(`${API_BASE}/leads`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(`${API_BASE}/classified-lead-subscriptions`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            ]);

            const data = await leadsRes.json();
            let subs: any[] = [];
            if (subsRes.ok) {
                subs = await subsRes.json();
            }

            let pipelineLeads: Lead[] = [];
            if (leadsRes.ok) {
                pipelineLeads = (Array.isArray(data) ? data : []).filter(l => 
                    (l.status !== 'New Inquiry' && l.status !== 'Requirement') || 
                    (l.unlockedBy && l.unlockedBy.includes(user?._id || ''))
                );
            }

            // Check if active subscription exists
            const activeSub = (Array.isArray(subs) ? subs : []).find(s => s.status === 'approved');
            if (activeSub) {
                // Fetch classified properties and map them as Leads
                const cpRes = await fetch(`${API_BASE}/classified-properties`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (cpRes.ok) {
                    const cpData = await cpRes.json();
                    if (Array.isArray(cpData)) {
                        const classifiedLeads: Lead[] = cpData.map(cp => ({
                            _id: cp._id,
                            name: cp.buildingName || cp.propertyType || 'Classified Property',
                            mobile: 'View in Details',
                            email: 'View in Details',
                            status: 'Classified Lead',
                            source: 'Classified',
                            city: cp.city || '',
                            budget: cp.price ? `₹${cp.price}` : '',
                            propertyType: cp.propertyType || '',
                            suggestedProjects: cp.owner ? [{
                                _id: cp._id,
                                name: cp.buildingName || cp.propertyType || 'Classified',
                                developer: {
                                    name: cp.owner.name || 'Owner',
                                    email: cp.owner.email || '',
                                    phone: cp.owner.phone || ''
                                }
                            }] : [],
                            timeline: [{
                                status: 'Classified',
                                notes: 'Dynamic Lead Classified Property added from Subscription.',
                                date: cp.createdAt
                            }],
                            createdAt: cp.createdAt
                        }));
                        pipelineLeads = [...pipelineLeads, ...classifiedLeads];
                    }
                }
            }

            setLeads(pipelineLeads);
        } catch (err) {
            toast.error('Failed to load leads');
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetail = async (id: string) => {
        const localMatch = leads.find(l => l._id === id);
        if (localMatch && localMatch.status === 'Classified Lead') {
            setViewLead(localMatch);
            return;
        }

        try {
            const res = await fetch(`${API_BASE}/leads/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (res.ok) {
                setViewLead(data);
            }
        } catch (err) {
            toast.error('Failed to load lead details');
        }
    };

    const handleUpdateStatus = async (id: string, status: string) => {
        try {
            const res = await fetch(`${API_BASE}/leads/${id}/status`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ status })
            });
            if (res.ok) {
                toast.success('Pipeline status updated');
                if (status === 'New Inquiry' || status === 'Requirement') {
                    // Move back to Inquiries inbox
                    setLeads(prev => prev.filter(l => l._id !== id));
                } else {
                    setLeads(prev => prev.map(l => l._id === id ? { ...l, status } : l));
                }
            }
        } catch (err) {
            toast.error('Error updating status');
        }
    };

    const filtered = leads.filter(l => {
        const matchSearch = l.name.toLowerCase().includes(search.toLowerCase()) || 
                           l.mobile.includes(search);
        let matchStatus = statusFilter === 'All' || l.status === statusFilter;
        if (statusFilter === 'unlocked') {
            matchStatus = !!(l.unlockedBy && l.unlockedBy.includes(user?._id || ''));
        }
        return matchSearch && matchStatus;
    });

    const counts = {
        all: leads.length,
        unlocked: leads.filter(l => l.unlockedBy && l.unlockedBy.includes(user?._id || '')).length,
        visit: leads.filter(l => l.status === 'Visit Scheduled').length,
        negotiation: leads.filter(l => l.status === 'Negotiation').length,
    };

    return (
        <UserDashboardLayout>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h2 className="text-3xl font-black text-blue-950 uppercase italic tracking-tighter">Lead Pipeline</h2>
                    <p className="text-blue-950/60 font-black uppercase tracking-[3px] text-[10px]">Convert your potential inquiries into successful property dealings.</p>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                {[
                    { label: 'Total Pipeline', count: counts.all, icon: 'fa-users', color: 'bg-blue-950', shadow: 'shadow-blue-900/20', filter: 'All' },
                    { label: 'Unlocked Leads', count: counts.unlocked, icon: 'fa-unlock-keyhole', color: 'bg-indigo-900', shadow: 'shadow-indigo-900/20', filter: 'unlocked' },
                    { label: 'Scheduled Visits', count: counts.visit, icon: 'fa-calendar-check', color: 'bg-amber-600', shadow: 'shadow-amber-900/20', filter: 'Visit Scheduled' },
                    { label: 'Active Negotiation', count: counts.negotiation, icon: 'fa-bolt', color: 'bg-emerald-600', shadow: 'shadow-emerald-900/20', filter: 'Negotiation' },
                ].map((s, i) => (
                    <button 
                        key={i}
                        onClick={() => setStatusFilter(s.filter)}
                        className={`relative overflow-hidden group p-6 rounded-3xl border transition-all text-left ${
                            statusFilter === s.filter 
                            ? 'bg-white border-blue-950 ring-4 ring-blue-500/10' 
                            : 'bg-white border-blue-900/5 hover:border-blue-950/20 shadow-sm'
                        }`}
                    >
                        <div className={`w-12 h-12 rounded-2xl ${s.color} ${s.shadow} shadow-lg flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform shadow-inner`}>
                            <i className={`fa-solid ${s.icon} text-lg`}></i>
                        </div>
                        <p className="text-[9px] font-black uppercase tracking-[3px] text-blue-950/40 mb-1 leading-none">{s.label}</p>
                        <h4 className="text-3xl font-black text-blue-950 leading-none italic tracking-tighter">{s.count}</h4>
                    </button>
                ))}
            </div>

            <div className="bg-white rounded-[2rem] border border-blue-900/10 shadow-sm overflow-hidden mb-12">
                <div className="p-8 border-b border-blue-900/5 flex flex-col md:flex-row justify-between items-center gap-6 bg-blue-50/10">
                    <div className="flex items-center gap-4 w-full md:w-auto">
                        <div className="relative group flex-1 md:w-[350px]">
                            <div className="absolute inset-y-0 left-0 pl-6 flex items-center pointer-events-none">
                                <i className="fa-solid fa-magnifying-glass text-blue-950/20 group-focus-within:text-blue-950 transition-colors"></i>
                            </div>
                            <input 
                                type="text" 
                                className="w-full bg-white border border-blue-900/10 rounded-xl py-4 pl-14 pr-6 text-sm font-black text-blue-950 placeholder:text-blue-950/20 focus:ring-4 focus:ring-blue-500/5 focus:border-blue-950 transition-all uppercase tracking-tight"
                                placeholder="Find lead by name, phone..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    {loading ? (
                        <div className="p-32 text-center text-blue-950/20 font-black uppercase tracking-[5px] text-[10px]">
                            <div className="w-12 h-12 border-4 border-blue-950 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
                            Syncing Pipeline...
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="p-32 text-center">
                            <div className="w-24 h-24 bg-blue-50/50 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-inner border border-blue-900/5 text-blue-950/10">
                                <i className="fa-solid fa-user-slash text-4xl"></i>
                            </div>
                            <h3 className="text-xl font-black text-blue-950 mb-2 uppercase italic">No Leads Found</h3>
                            <p className="text-blue-950/40 text-[10px] font-black uppercase tracking-widest max-w-xs mx-auto">Either your filters are too restrictive or you have no active inquiries.</p>
                        </div>
                    ) : (
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-blue-50/5 border-b border-blue-900/5">
                                    <th className="px-8 py-5 text-[10px] font-black text-blue-950/40 uppercase tracking-[3px]">Project Partner</th>
                                    <th className="px-8 py-5 text-[10px] font-black text-blue-950/40 uppercase tracking-[3px]">Project Detail</th>
                                    <th className="px-8 py-5 text-[10px] font-black text-blue-950/40 uppercase tracking-[3px]">Partner Contact</th>
                                    <th className="px-8 py-5 text-[10px] font-black text-blue-950/40 uppercase tracking-[3px] text-right">View</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-blue-900/5">
                                {filtered.map(l => {
                                    const project = l.suggestedProjects?.[0] || l.siteVisit?.project;
                                    const dev = project?.developer;
                                    
                                    return (
                                        <tr key={l._id} className="group hover:bg-blue-50/20 transition-all">
                                            <td className="px-8 py-7">
                                                <div className="flex items-center gap-4 text-left">
                                                    <div className="relative">
                                                        <div className="w-12 h-12 rounded-xl bg-blue-950 flex items-center justify-center text-white font-black text-lg shadow-lg shadow-blue-900/20 ring-4 ring-white">
                                                            {(dev?.name || project?.name || 'P').charAt(0).toUpperCase()}
                                                        </div>
                                                        {l.unlockedBy && l.unlockedBy.includes(user?._id || '') && (
                                                            <div className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center border-2 border-white shadow-md animate-pulse">
                                                                <i className="fa-solid fa-crown text-[8px] text-white"></i>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="text-left">
                                                        <h4 className="font-black text-blue-950 group-hover:text-blue-600 transition-colors uppercase text-sm tracking-tight italic leading-tight">{dev?.name || l.name || 'Authorized Partner'}</h4>
                                                        <p className="text-[10px] font-black text-blue-950/40 uppercase mt-1 tracking-widest leading-none">{project?.name || 'General Inquiry'}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-8 py-7">
                                                <div className="flex flex-col gap-1">
                                                    <span className="text-[10px] font-black text-blue-950 uppercase tracking-tight italic opacity-100">{dev?.email || 'email@partner.com'}</span>
                                                    <div className="flex items-center gap-1.5 mt-1 bg-blue-50 w-fit px-2 py-1 rounded-lg border border-blue-100">
                                                        <div className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-pulse"></div>
                                                        <span className="text-[10px] font-black text-blue-600 uppercase italic leading-none">{dev?.phone || '+91 00000 00000'}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-8 py-7">
                                                <div className="flex items-center gap-2">
                                                    <a href={`tel:${dev?.phone}`} className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center hover:bg-emerald-600 hover:text-white transition-all shadow-sm active:scale-90">
                                                        <i className="fa-solid fa-phone text-[10px]"></i>
                                                    </a>
                                                    <a href={`https://wa.me/91${dev?.phone?.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-xl bg-green-50 text-green-600 flex items-center justify-center hover:bg-green-600 hover:text-white transition-all shadow-sm active:scale-90">
                                                        <i className="fa-brands fa-whatsapp text-[10px]"></i>
                                                    </a>
                                                </div>
                                            </td>
                                            <td className="px-8 py-7 text-right">
                                                {l.status === 'Classified Lead' ? (
                                                    <button 
                                                        onClick={() => handleViewDetail(l._id)}
                                                        className="w-11 h-11 rounded-xl bg-blue-50/50 text-blue-950/20 hover:bg-blue-950 hover:text-white transition-all flex items-center justify-center shadow-sm ml-auto active:scale-90 border border-blue-900/5"
                                                    >
                                                        <i className="fa-solid fa-eye text-sm"></i>
                                                    </button>
                                                ) : (
                                                    <Link 
                                                        to={`/project-details/${project?.slug || project?._id}`}
                                                        className="w-11 h-11 rounded-xl bg-blue-50/50 text-blue-950/20 hover:bg-blue-950 hover:text-white transition-all flex items-center justify-center shadow-sm ml-auto active:scale-90 border border-blue-900/5"
                                                    >
                                                        <i className="fa-solid fa-eye text-sm"></i>
                                                    </Link>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Detailed Overlay */}
            {viewLead && (
                <div className="fixed inset-0 z-[1100] flex items-center justify-center px-4 overflow-hidden">
                    <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm" onClick={() => setViewLead(null)}></div>
                    <div className="relative w-full max-w-4xl bg-white rounded-[2rem] shadow-2xl overflow-hidden max-h-[90vh] flex flex-col border border-blue-900/10">
                        <div className="p-10 border-b border-blue-900/5 flex items-center justify-between bg-blue-50/10">
                            <div className="flex items-center gap-6">
                                <div className="w-20 h-20 rounded-2xl bg-blue-950 flex items-center justify-center text-white text-4xl font-black italic shadow-xl shadow-blue-900/40 ring-4 ring-white">
                                    {viewLead.name.charAt(0).toUpperCase()}
                                </div>
                                <div className="text-left">
                                    <h2 className="text-3xl font-black text-blue-950 uppercase tracking-tight italic">{viewLead.name}</h2>
                                    <p className="text-[10px] font-black text-blue-600 uppercase tracking-[4px] mt-1 opacity-60">Lead ID: {viewLead._id.slice(-8)}</p>
                                </div>
                            </div>
                            <button onClick={() => setViewLead(null)} className="w-12 h-12 rounded-xl bg-white text-blue-950/20 hover:text-blue-950 hover:bg-blue-50 transition-all flex items-center justify-center shadow-sm active:scale-90">
                                <i className="fa-solid fa-xmark text-xl"></i>
                            </button>
                        </div>

                        <div className="p-10 overflow-y-auto flex-grow grid grid-cols-1 md:grid-cols-2 gap-12">
                            <div className="space-y-8">
                                <div>
                                    <p className="text-[10px] font-black text-blue-950/40 uppercase tracking-[3px] mb-6">Contact Gateway</p>
                                    <div className="grid grid-cols-1 gap-4">
                                        <div className="p-6 rounded-2xl bg-blue-50/20 border border-blue-900/5 flex items-center justify-between group/card hover:bg-blue-50 transition-all">
                                            <div className="text-left">
                                                <p className="text-[8px] font-black text-blue-950/40 uppercase tracking-[3px] mb-1">Mobile Access</p>
                                                <p className="text-base font-black text-blue-950 uppercase italic tracking-tight">{viewLead.mobile}</p>
                                            </div>
                                            <a href={`tel:${viewLead.mobile}`} className="w-11 h-11 rounded-xl bg-blue-950 text-white flex items-center justify-center shadow-lg shadow-blue-900/40 group-hover/card:scale-110 transition-transform">
                                                <i className="fa-solid fa-phone text-xs"></i>
                                            </a>
                                        </div>
                                        <div className="p-6 rounded-2xl bg-blue-50/20 border border-blue-900/5 flex items-center justify-between group/card hover:bg-blue-50 transition-all">
                                            <div className="text-left">
                                                <p className="text-[8px] font-black text-blue-950/40 uppercase tracking-[3px] mb-1">Email Node</p>
                                                <p className="text-base font-black text-blue-950 uppercase italic tracking-tight truncate max-w-[180px]">{viewLead.email || 'Not Provided'}</p>
                                            </div>
                                            <a href={`mailto:${viewLead.email}`} className="w-11 h-11 rounded-xl bg-blue-950 text-white flex items-center justify-center shadow-lg shadow-blue-900/40 group-hover/card:scale-110 transition-transform">
                                                <i className="fa-solid fa-envelope text-xs"></i>
                                            </a>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <p className="text-[10px] font-black text-blue-950/40 uppercase tracking-[3px] mb-6">Pipeline Synchronization</p>
                                    <div className="p-8 rounded-[2rem] bg-blue-950 text-white mb-8 relative overflow-hidden group/stage">
                                        <div className="absolute top-0 right-0 w-32 h-32 bg-blue-600/20 blur-3xl rounded-full group-hover/stage:scale-150 transition-transform duration-1000"></div>
                                        <h4 className="text-[9px] font-black uppercase tracking-[4px] text-blue-400 mb-3 relative z-10">Current Conversion Stage</h4>
                                        <h2 className="text-4xl font-black text-white italic tracking-tighter relative z-10 leading-none mb-8">{viewLead.status}</h2>
                                        <div className="mt-8 relative z-10">
                                            <a 
                                                href={`https://wa.me/91${viewLead.mobile.replace(/\D/g, '')}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="block w-full bg-white text-blue-950 py-5 rounded-2xl font-black text-[10px] uppercase tracking-[4px] text-center shadow-2xl transition-all hover:bg-blue-50 active:scale-95"
                                            >
                                                WhatsApp Chat Sync
                                            </a>
                                        </div>
                                    </div>

                                    {/* DEVELOPER / PROJECT PARTNER CONTACT */}
                                    {(viewLead.suggestedProjects || viewLead.siteVisit?.project) && (
                                        <div className="p-8 rounded-[2rem] bg-indigo-50/50 border border-indigo-100">
                                            <div className="flex items-center justify-between mb-8">
                                                <div className="text-left">
                                                    <p className="text-[9px] font-black text-indigo-400 uppercase tracking-[3px] mb-2">Asset Partner Contact</p>
                                                    <h4 className="text-xl font-black text-blue-950 uppercase italic tracking-tight leading-tight">
                                                        {(viewLead.suggestedProjects?.[0]?.developer?.name || viewLead.siteVisit?.project?.developer?.name || 'Project Admin')}
                                                    </h4>
                                                </div>
                                                <div className="w-12 h-12 rounded-xl bg-blue-950 text-white flex items-center justify-center shadow-xl shadow-blue-900/40 shrink-0">
                                                    <i className="fa-solid fa-building-user text-lg"></i>
                                                </div>
                                            </div>
                                            
                                            <div className="space-y-4 mb-8">
                                                <div className="flex items-center gap-4 bg-white p-3 rounded-xl border border-indigo-100 shadow-sm">
                                                    <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 flex-shrink-0">
                                                        <i className="fa-solid fa-phone text-[10px]"></i>
                                                    </div>
                                                    <p className="text-xs font-black text-blue-950 uppercase tracking-tight italic">{(viewLead.suggestedProjects?.[0]?.developer?.phone || viewLead.siteVisit?.project?.developer?.phone || 'No Phone')}</p>
                                                </div>
                                                <div className="flex items-center gap-4 bg-white p-3 rounded-xl border border-indigo-100 shadow-sm">
                                                    <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 flex-shrink-0">
                                                        <i className="fa-solid fa-envelope text-[10px]"></i>
                                                    </div>
                                                    <p className="text-xs font-black text-blue-950 lowercase tracking-tight italic truncate">{(viewLead.suggestedProjects?.[0]?.developer?.email || viewLead.siteVisit?.project?.developer?.email || 'No Email')}</p>
                                                </div>
                                            </div>

                                            <Link 
                                                to={`/project-details/${viewLead.suggestedProjects?.[0]?.slug || viewLead.suggestedProjects?.[0]?._id || viewLead.siteVisit?.project?.slug || viewLead.siteVisit?.project?._id}`}
                                                className="block w-full py-4 bg-white border border-blue-950/20 rounded-xl text-center text-[10px] font-black text-blue-950 uppercase tracking-[3px] hover:bg-blue-950 hover:text-white transition-all shadow-sm active:scale-95"
                                            >
                                                View Project Overview
                                            </Link>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-8">
                                <div className="text-left">
                                    <p className="text-[10px] font-black text-blue-950/40 uppercase tracking-[3px] mb-8">Journey Timeline</p>
                                    <div className="space-y-6 relative before:absolute before:left-3.5 before:top-2 before:bottom-2 before:w-1 before:bg-blue-50">
                                        {(viewLead.timeline || []).slice().reverse().map((t, i) => (
                                            <div key={i} className="flex gap-8 relative">
                                                <div className="w-7 h-7 rounded-full bg-white border-4 border-blue-950 shrink-0 z-10 shadow-sm"></div>
                                                <div className="p-6 rounded-2xl bg-blue-50/20 border border-blue-900/5 flex-grow text-left">
                                                    <div className="flex justify-between items-center mb-2">
                                                        <h5 className="text-[11px] font-black text-blue-950 uppercase tracking-[2px] italic">{t.status}</h5>
                                                        <span className="text-[9px] font-black text-blue-950/40 uppercase tracking-widest">{new Date(t.date).toLocaleDateString()}</span>
                                                    </div>
                                                    <p className="text-xs text-blue-950/60 font-medium italic">"{t.notes}"</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-10 bg-blue-50/30 border-t border-blue-900/5 flex justify-end">
                            <button className="px-12 py-5 rounded-xl bg-blue-950 text-white font-black uppercase tracking-[4px] text-[10px] shadow-2xl shadow-blue-900/40 hover:scale-105 active:scale-95 transition-all" onClick={() => setViewLead(null)}>
                                Close Record
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </UserDashboardLayout>
    );
}
